/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { OnChangeEventContext } from 'react-crane'

import { BoardGroupFragment, PlanningTerm } from '../../../../../generated/graphql'
import { borderColor } from '../../../../../lib/styles/colors'
import { Select } from '../../../../shared/input'
import { LabelVisibility } from '../../../../../types'

const termCss = css`
  margin-bottom: -8px;
  margin-left: -4px;
  padding-bottom: 0;

  .crane-select {
    .crane-select-input-group {
      border: none;
      background: none;
      cursor: pointer;
    }

    &.open .crane-select-input-group {
      border: 1px solid ${borderColor};
      border-bottom: none;
      background-color: white;
    }

    &.focus:not(.open) .crane-select-input-group {
      border: none;
    }

    .crane-select-value-group {
      height: 47px;
      font-size: 16px;
      font-weight: 500;
      padding-top: 18px;
    }

    .crane-select-menu-container {
      top: 50px;
    }
  }

  .crane-select-value-group > span {
    position: relative;
  }

  .crane-select-value-group > span:after {
    left: 0;
    bottom: 1px;
    width: 100%;
    position: absolute;
    content: '';
  }

  .crane-select-input-group:after {
    bottom: 6px;
  }
`

const focusedCss = css`
  width: calc(100% - 10px);
`

export type TermSelectProps = {
  /** Current Plan Group */
  group: BoardGroupFragment
  /** If the name field is focused */
  isFocused: boolean
  /** Event callback when the name field is blurred */
  onBlur: () => void
  /** Event callback when the name field is focused */
  onFocus: () => void
  /** Event callback when term is changed */
  onTermChange: (e: OnChangeEventContext) => void
  /** Label to use for term */
  termLabel: string
  /** List of terms to display */
  terms: Array<PlanningTerm>
}

const TermSelect = ({
  group,
  isFocused,
  onBlur,
  onFocus,
  onTermChange,
  termLabel,
  terms
}: TermSelectProps) => {
  const { externalTermId, id } = group

  const calculateWidth = () => {
    let width = 15
    const term = terms?.find((t) => t.termCode === externalTermId)

    if (term) {
      width = term.description.length + 5
    }

    return width
  }

  const unfocusedCss = css`
    width: ${calculateWidth()}ch;
  `

  // If for some reason there's no externalTermId (ie: bad plan data),
  // fall back to showing the group title as the placeholder text
  const placeholderText = !externalTermId && group.title ? group.title : `Select ${termLabel}`

  return (
    <Select
      css={[termCss, isFocused ? focusedCss : unfocusedCss]}
      id={`${id}-term`}
      label={termLabel}
      labelKey="description"
      labelVisibility={LabelVisibility.VISIBLE_ON_FOCUS}
      name={termLabel}
      onDropdownBlur={onBlur}
      onChange={onTermChange}
      onDropdownFocus={onFocus}
      options={terms}
      placeholder={placeholderText}
      value={externalTermId}
      valueKey="termCode"
    />
  )
}

export default TermSelect
